import { createContext, useContext } from 'react';
import type { User } from '@/types/api';

/**
 * Who sees a `PendingDecision` marker: staff only (Mario, 2026-10-14).
 *
 * The marker's popover is written for TRF ("They are drafts for Melanie…"),
 * so a family reading their own status page met staff notes about it. A
 * family never sees one now; staff still do, on every surface.
 *
 * The viewer is the OPERATOR — the staff member running an impersonation, not
 * the family being viewed — so staff looking at a family screen as that family
 * still get the markers the screen carries. Roles are the HELD roles
 * (`user.roles`), not the active one (DECISION 2, 2026-09-21): an admin who has
 * switched to the parent role is still staff.
 */
export function seesPendingDecisions(user: User | null, operator: User | null): boolean {
  const viewer = operator ?? user;
  if (!viewer) return false;
  const held = viewer.roles?.length ? viewer.roles : [viewer.role];
  return held.some((role) => role === 'trfer' || role === 'admin');
}

/**
 * Set once at the app root from the real session (`PendingDecisionAudience`),
 * so a preview that overrides the session for its subtree (signed-out or copy
 * previews on staff screens) still shows its markers to the staff member
 * looking at it. Defaults to visible: only a bare render (a unit test) has no
 * provider, and the marker's own tests render it bare.
 */
export const PendingDecisionAudienceContext = createContext<boolean>(true);

export function usePendingDecisionsVisible(): boolean {
  return useContext(PendingDecisionAudienceContext);
}
